/**
 * Identity providers whose accounts are accepted without Firebase's own email
 * verification step.
 *
 * Firebase sets `emailVerified` only when the address was confirmed through its
 * verification email. It does not set it for an account that signed in through
 * Microsoft, because Microsoft's token carries no such claim, even though the
 * provider has authenticated the account holder and the address is the one the
 * organisation issued. Which organisations are admitted is decided by the
 * provider's tenant setting in `@/shared/services/firebase`, not here.
 */
export const FEDERATED_PROVIDER_IDS = ['microsoft.com'];

/**
 * Whether a signed-in Firebase user's email address can be trusted.
 *
 * Every gate that decides "is this person verified" reads this rather than
 * `user.emailVerified`, so the rule lives in one place.
 *
 * @param {import('firebase/auth').User | null | undefined} user
 * @returns {boolean}
 */
export function isEmailVerified(user) {
    // An account with no address cannot have a verified one. This also holds
    // the login page in place while a Microsoft account that shared no email
    // is signed out again, so the message explaining why is not lost to a
    // redirect.
    if (!user || !user.email) {
        return false;
    }
    if (user.emailVerified) {
        return true;
    }
    // A federated identity proves the address IT carries, so it counts only
    // when that address is the account's. The server applies the same rule to
    // a password session on a linked account; the two must agree, or the
    // browser shows a verified account whose sessions the API refuses.
    const address = String(user.email).toLowerCase();
    const providers = Array.isArray(user.providerData) ? user.providerData : [];
    return providers.some(
        (entry) =>
            entry &&
            FEDERATED_PROVIDER_IDS.includes(entry.providerId) &&
            typeof entry.email === 'string' &&
            entry.email.toLowerCase() === address
    );
}
